// ============================================================================
// PackVersionHistoryDrawer — READ-ONLY in-context Pack version history
// ----------------------------------------------------------------------------
// Shows the lifecycle of one logical Pack (draft / published / new version /
// archived) across all of its version documents. Opening it performs READS ONLY
// (getPackVersions → getPacksBySpecialty); it never writes, never mutates a pack
// or referral, and never creates a new version.
//
// Theming: styled entirely with theme-aware CSS variable tokens so it matches
// both light and dark themes. It reuses the Pack Builder guidance-drawer family
// (--rg-help-*) for chrome + cards — so this drawer reads as a sibling of the
// existing HelpDrawer (ivory/warm in light, navy in dark) — and the semantic
// --rg-status-* tokens for the status pills/banners. No hardcoded hex/rgba.
//
// Actor is shown as the raw createdByUid for this first build (no user lookup).
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { safeUserError } from '../core/safeError.js';
import { X, Clock, AlertTriangle, Loader2, RotateCcw, CheckCircle2 } from 'lucide-react';
import { getPackVersions } from '../core/store.packs.js';
import { derivePackVersionRows, hasMultipleActiveVersions, toMillis } from '../core/packVersionHistory.js';
import { formatLastUpdated } from '../core/formatters.js';

// Theme-aware status pill styling (tokens adapt per html[data-theme]).
const STATUS_STYLE = {
  active:   { bg: 'var(--rg-status-success-bg)', color: 'var(--rg-status-success-text)', border: 'var(--rg-status-success-border)', label: 'Active' },
  draft:    { bg: 'var(--rg-status-warning-bg)', color: 'var(--rg-status-warning-text)', border: 'var(--rg-status-warning-border)', label: 'Draft' },
  archived: { bg: 'var(--rg-status-neutral-bg)', color: 'var(--rg-status-neutral-text)', border: 'var(--rg-status-neutral-border)', label: 'Archived' },
};

const pad = (n) => String(n).padStart(2, '0');

const fmtAbsolute = (ts) => {
  const ms = toMillis(ts);
  if (ms == null) return null;
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtRelative = (ts) => {
  const ms = toMillis(ts);
  return ms == null ? null : formatLastUpdated(new Date(ms));
};

function VersionRow({ row }) {
  const st = STATUS_STYLE[row.status] || STATUS_STYLE.draft;
  const abs = fmtAbsolute(row.occurredAt);
  const rel = fmtRelative(row.occurredAt);
  return (
    <div style={{ border: '1px solid var(--rg-help-accent-border)', borderRadius: 8, padding: 14, marginBottom: 10, background: 'var(--rg-help-accent-bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--rg-help-text-strong)' }}>v{row.versionId || '—'}</span>
          <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 999, background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
            {st.label}
          </span>
          {row.isCurrentActive && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'var(--rg-status-success-bg)', color: 'var(--rg-status-success-text)', border: '1px solid var(--rg-status-success-border)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <CheckCircle2 size={11} /> Current active
            </span>
          )}
        </div>
      </div>
      <p style={{ fontSize: 13, color: 'var(--rg-help-text)', margin: '0 0 4px 0' }}>{row.eventLabel}</p>
      <div style={{ fontSize: 12, color: 'var(--rg-help-label)', lineHeight: 1.5 }}>
        {abs && <div>{abs}{rel ? ` · ${rel}` : ''}</div>}
        {!abs && <div>Time not recorded</div>}
        <div>By {row.actorUid || '—'}</div>
        {row.supersedesVersionId && <div>Supersedes v{row.supersedesVersionId}</div>}
        {row.supersededByVersionId && <div>Superseded by v{row.supersededByVersionId}</div>}
      </div>
    </div>
  );
}

export default function PackVersionHistoryDrawer({ tenantId, specialtyId, packId, packName, onClose }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !specialtyId || !packId) {
      setError('Version history is unavailable for this Pack (missing pack identifiers).');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const docs = await getPackVersions(tenantId, specialtyId, packId);
      setRows(derivePackVersionRows(docs));
    } catch (err) {
      console.error('[PackVersionHistory] load failed:', err.message, err);
      setError(safeUserError(err));
    } finally {
      setLoading(false);
    }
  }, [tenantId, specialtyId, packId]);

  useEffect(() => { load(); }, [load]);

  const multipleActive = Array.isArray(rows) && hasMultipleActiveVersions(rows);

  return (
    <>
      {/* Overlay — theme-aware scrim (soft navy-tinted in light, not dirty black) */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'var(--rg-help-scrim)', zIndex: 60 }}
      />
      {/* Panel — ivory/warm in light, navy in dark (matches the guidance drawer) */}
      <div
        role="dialog"
        aria-label="Pack version history"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: '440px', maxWidth: '92vw',
          background: 'var(--rg-help-bg)', borderLeft: '1px solid var(--rg-help-border)',
          zIndex: 61, display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 24px rgba(2, 6, 23, 0.25)',
        }}
      >
        {/* Header */}
        <div style={{ padding: 16, borderBottom: '1px solid var(--rg-help-divider)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--rg-help-text-strong)', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              <Clock size={16} /> Version History
            </h3>
            <p style={{ fontSize: 12, color: 'var(--rg-help-label)', margin: '4px 0 0 0' }}>
              {packName || 'Pack'} · read-only
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--rg-help-label)' }} title="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--rg-help-label)', fontSize: 13 }}>
              <Loader2 size={16} className="animate-spin" /> Loading version history…
            </div>
          )}

          {!loading && error && (
            <div style={{ background: 'var(--rg-status-warning-bg)', border: '1px solid var(--rg-status-warning-border)', borderRadius: 8, padding: 14 }}>
              <p style={{ fontSize: 13, color: 'var(--rg-status-warning-text)', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={14} /> {error}
              </p>
              <button
                onClick={load}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--rg-help-border)', background: 'transparent', color: 'var(--rg-help-text-strong)', cursor: 'pointer' }}
              >
                <RotateCcw size={13} /> Retry
              </button>
            </div>
          )}

          {!loading && !error && Array.isArray(rows) && rows.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--rg-help-label)' }}>
              <Clock size={36} style={{ opacity: 0.4, marginBottom: 12 }} />
              <p style={{ fontSize: 13, margin: 0 }}>No version history for this Pack.</p>
            </div>
          )}

          {!loading && !error && Array.isArray(rows) && rows.length > 0 && (
            <>
              {multipleActive && (
                <div style={{ background: 'var(--rg-status-warning-bg)', border: '1px solid var(--rg-status-warning-border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <p style={{ fontSize: 12, color: 'var(--rg-status-warning-text)', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AlertTriangle size={14} /> More than one active version exists for this Pack — this is unexpected.
                  </p>
                </div>
              )}
              {rows.map((row) => (
                <VersionRow key={row.packDocId || `${row.packId}__${row.versionId}`} row={row} />
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
}
